// VENDORED from text-quiet-companion with SURGICAL ownership diffs only:
//   1. De-SSR: shellComponent/HeadContent/Scripts and the styles.css?url
//      link are gone — the SPA shell lives in index.html; styles.css is
//      imported by src/main.tsx. head() meta moved to index.html.
//   2. Lovable telemetry stripped (reportLovableError → console.error).
//   3. AUTH GATE added: no session → redirect to /login (demo mode does
//      NOT bypass sign-in; family beta is allowlisted server-side).
//   4. Demo badge: persistent "Demo data" pill when VITE_LIFE_MODE=demo.
// Everything else — 404/error components, root outlet — is verbatim.

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

import { subscribeAuth, firebaseAuth } from "../lib/firebase";
import { IS_DEMO } from "../lib/mode";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

/** Ownership diff 4: honest-publish badge, demo mode only. */
function DemoBadge() {
  if (!IS_DEMO) return null;
  return (
    <div
      role="status"
      className="fixed bottom-3 right-3 z-50 rounded-full px-3 py-1.5 text-xs font-medium shadow-lg"
      style={{
        backgroundColor: "var(--dryvn-navy, #0A2A5C)",
        color: "#FFFFFF",
        borderLeft: "3px solid #7BD3C8",
      }}
    >
      Demo data — your real data arrives at launch.
    </div>
  );
}

/**
 * Ownership diff 3: route guard. Firebase session required everywhere
 * except /login. When Firebase isn't configured (no VITE_FIREBASE_*),
 * the gate still routes to /login, which explains the configuration
 * state — never a silent bypass.
 */
function AuthGate({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  // DEMO mode is a zero-dependency public preview (fixtures, no API calls,
  // persistent "Demo data" badge). It renders WITHOUT Firebase sign-in so the
  // demo URL is directly shareable and screenshot-able. This bypass is scoped
  // strictly to IS_DEMO — LIVE mode keeps the full session gate below (the
  // family beta is still allowlisted server-side on every /api/life/* call).
  const [state, setState] = useState<"loading" | "in" | "out">(
    IS_DEMO ? "in" : firebaseAuth() ? "loading" : "out",
  );

  useEffect(() => {
    if (IS_DEMO) return;
    return subscribeAuth((u) => setState(u ? "in" : "out"));
  }, []);

  useEffect(() => {
    if (IS_DEMO) return;
    if (state === "out" && pathname !== "/login") {
      void navigate({ to: "/login" });
    }
  }, [state, pathname, navigate]);

  if (IS_DEMO) return <>{children}</>;
  if (pathname === "/login") return <>{children}</>;
  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Signing you in…
      </div>
    );
  }
  if (state === "out") return null; // redirecting
  return <>{children}</>;
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthGate>
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
      </AuthGate>
      <DemoBadge />
    </QueryClientProvider>
  );
}
